import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

/* ──────────────────────────────────────────────────────────────
   FLOW FIELD CANVAS
   A sum-of-sines noise field drives particles that trace paths in
   magenta→violet. Mouse creates a vortex. Canvas-2d so it runs
   everywhere; particle count + vortex scale down on low-power devices.
   ────────────────────────────────────────────────────────────── */
const FlowField = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isTouch = window.matchMedia('(pointer: coarse)').matches;
    const lowPower = isTouch || window.innerWidth < 768;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.8);

    let w = 0;
    let h = 0;
    let particles = [];
    const mouse = { x: -9999, y: -9999, active: false };
    let raf;
    let t0 = 0;

    const noiseAngle = (x, y, t) => {
      const a =
        Math.sin(x * 0.0021 + t * 0.00015) * 1.0 +
        Math.sin(y * 0.0019 + t * 0.00021) * 1.0 +
        Math.sin((x + y) * 0.0013 + t * 0.00018) * 0.6;
      return a * Math.PI * 0.9;
    };

    class Particle {
      constructor() {
        this.reset(true);
      }

      reset(initial = false) {
        this.x = Math.random() * w;
        this.y = Math.random() * h;
        this.life = initial ? Math.random() * 400 : 0;
        this.maxLife = 280 + Math.random() * 220;
        this.size = 0.9 + Math.random() * 1.6;
        this.hueShift = Math.random();
      }

      step(t) {
        const angle = noiseAngle(this.x, this.y, t);
        let vx = Math.cos(angle) * 1.1;
        let vy = Math.sin(angle) * 1.1;

        if (mouse.active) {
          const dx = this.x - mouse.x;
          const dy = this.y - mouse.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < 240 * 240) {
            const d = Math.sqrt(d2) || 1;
            const f = (1 - d / 240) * 2.4;
            vx += (-dy / d) * f;
            vy += (dx / d) * f;
            vx += (-dx / d) * f * 0.18;
            vy += (-dy / d) * f * 0.18;
          }
        }

        this.x += vx;
        this.y += vy;
        this.life += 1;
        if (
          this.life > this.maxLife ||
          this.x < -10 || this.x > w + 10 ||
          this.y < -10 || this.y > h + 10
        ) {
          this.reset();
        }
      }

      draw() {
        const alpha =
          Math.min(1, this.life / 40) * (1 - Math.min(1, this.life / this.maxLife));
        const r = Math.floor(209 + (124 - 209) * this.hueShift);
        const g = Math.floor(0 + (58 - 0) * this.hueShift);
        const b = Math.floor(163 + (237 - 163) * this.hueShift);
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha * 0.42})`;
        ctx.fillRect(this.x, this.y, this.size, this.size);
      }
    }

    const resize = () => {
      const parent = canvas.parentElement;
      w = parent.clientWidth;
      h = parent.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const cap = reduced ? 80 : lowPower ? 130 : 360;
      const divisor = lowPower ? 11000 : 6500;
      const target = Math.min(cap, Math.floor((w * h) / divisor));
      if (particles.length < target) {
        while (particles.length < target) particles.push(new Particle());
      } else {
        particles = particles.slice(0, target);
      }
    };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
      mouse.active = true;
    };
    const onLeave = () => {
      mouse.active = false;
      mouse.x = -9999;
      mouse.y = -9999;
    };

    const tick = (t) => {
      if (!t0) t0 = t;
      ctx.fillStyle = 'rgba(9, 9, 11, 0.08)';
      ctx.fillRect(0, 0, w, h);
      for (let i = 0; i < particles.length; i += 1) {
        particles[i].step(t);
        particles[i].draw();
      }
      raf = window.requestAnimationFrame(tick);
    };

    resize();
    window.addEventListener('resize', resize);
    if (!isTouch) {
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseleave', onLeave);
    }

    if (reduced) {
      ctx.clearRect(0, 0, w, h);
      particles.forEach((p) => p.draw());
    } else {
      raf = window.requestAnimationFrame(tick);
    }

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return <canvas ref={canvasRef} className="fh-canvas" aria-hidden="true" />;
};

/* ──────────────────────────────────────────────────────────────
   FLUID HERO — GPU fluid sim (Navier–Stokes on WebGL fragment
   shaders). Magenta/violet dye splats follow the pointer; auto-
   splats keep it alive when idle. Falls back to FlowField via
   onFail when WebGL/float textures aren't available.
   Distilled from Jos Stam + Pavel Dobryakov's WebGL pattern.
   ────────────────────────────────────────────────────────────── */
const DYE_TARGET = (window.devicePixelRatio || 1) >= 1.75 ? 2048 : 1024;

const CFG = {
  SIM_RESOLUTION: 128,
  DYE_RESOLUTION: DYE_TARGET,
  DENSITY_DISSIPATION: 1.2,
  VELOCITY_DISSIPATION: 0.45,
  PRESSURE: 0.78,
  PRESSURE_ITERATIONS: 18,
  CURL: 26,
  SPLAT_RADIUS: 0.22,
  SPLAT_FORCE: 5200,
  AUTO_SPLAT_INTERVAL: 1600,
};

const PALETTE = [
  [0.82, 0.0, 0.64],
  [0.49, 0.23, 0.93],
  [0.96, 0.45, 0.82],
  [0.55, 0.3, 0.95],
];

const randSplatColor = () => {
  const c = PALETTE[Math.floor(Math.random() * PALETTE.length)];
  return [c[0] * 0.32, c[1] * 0.32, c[2] * 0.32];
};

const VS = `
precision highp float;
attribute vec2 aPosition;
varying vec2 vUv;
varying vec2 vL; varying vec2 vR; varying vec2 vT; varying vec2 vB;
uniform vec2 texelSize;
void main() {
  vUv = aPosition * 0.5 + 0.5;
  vL = vUv - vec2(texelSize.x, 0.0);
  vR = vUv + vec2(texelSize.x, 0.0);
  vT = vUv + vec2(0.0, texelSize.y);
  vB = vUv - vec2(0.0, texelSize.y);
  gl_Position = vec4(aPosition, 0.0, 1.0);
}`;

const FS_COPY = `
precision mediump float;
varying vec2 vUv;
uniform sampler2D uTexture;
void main() { gl_FragColor = texture2D(uTexture, vUv); }`;

const FS_CLEAR = `
precision mediump float;
varying vec2 vUv;
uniform sampler2D uTexture;
uniform float value;
void main() { gl_FragColor = value * texture2D(uTexture, vUv); }`;

const FS_DISPLAY = `
precision highp float;
varying vec2 vUv;
uniform sampler2D uTexture;
void main() {
  vec3 c = texture2D(uTexture, vUv).rgb;
  c = pow(c, vec3(0.85));
  vec2 q = vUv - 0.5;
  float vig = smoothstep(0.95, 0.18, length(q));
  gl_FragColor = vec4(c * vig, 1.0);
}`;

const FS_SPLAT = `
precision highp float;
varying vec2 vUv;
uniform sampler2D uTarget;
uniform float aspectRatio;
uniform vec3 color;
uniform vec2 point;
uniform float radius;
void main() {
  vec2 p = vUv - point.xy;
  p.x *= aspectRatio;
  vec3 splat = exp(-dot(p, p) / radius) * color;
  vec3 base = texture2D(uTarget, vUv).xyz;
  gl_FragColor = vec4(base + splat, 1.0);
}`;

const FS_ADVECTION = `
precision highp float;
precision highp sampler2D;
varying vec2 vUv;
uniform sampler2D uVelocity;
uniform sampler2D uSource;
uniform vec2 texelSize;
uniform vec2 dyeTexelSize;
uniform float dt;
uniform float dissipation;
vec4 bilerp(sampler2D sam, vec2 uv, vec2 tsize) {
  vec2 st = uv / tsize - 0.5;
  vec2 iuv = floor(st);
  vec2 fuv = fract(st);
  vec4 a = texture2D(sam, (iuv + vec2(0.5, 0.5)) * tsize);
  vec4 b = texture2D(sam, (iuv + vec2(1.5, 0.5)) * tsize);
  vec4 c = texture2D(sam, (iuv + vec2(0.5, 1.5)) * tsize);
  vec4 d = texture2D(sam, (iuv + vec2(1.5, 1.5)) * tsize);
  return mix(mix(a, b, fuv.x), mix(c, d, fuv.x), fuv.y);
}
void main() {
  vec2 coord = vUv - dt * bilerp(uVelocity, vUv, texelSize).xy * texelSize;
  vec4 result = bilerp(uSource, coord, dyeTexelSize);
  float decay = 1.0 + dissipation * dt;
  gl_FragColor = result / decay;
}`;

const FS_DIVERGENCE = `
precision mediump float;
precision mediump sampler2D;
varying vec2 vUv;
varying vec2 vL; varying vec2 vR; varying vec2 vT; varying vec2 vB;
uniform sampler2D uVelocity;
void main() {
  float L = texture2D(uVelocity, vL).x;
  float R = texture2D(uVelocity, vR).x;
  float T = texture2D(uVelocity, vT).y;
  float B = texture2D(uVelocity, vB).y;
  vec2 C = texture2D(uVelocity, vUv).xy;
  if (vL.x < 0.0) L = -C.x;
  if (vR.x > 1.0) R = -C.x;
  if (vT.y > 1.0) T = -C.y;
  if (vB.y < 0.0) B = -C.y;
  gl_FragColor = vec4(0.5 * (R - L + T - B), 0.0, 0.0, 1.0);
}`;

const FS_CURL = `
precision mediump float;
precision mediump sampler2D;
varying vec2 vL; varying vec2 vR; varying vec2 vT; varying vec2 vB;
uniform sampler2D uVelocity;
void main() {
  float L = texture2D(uVelocity, vL).y;
  float R = texture2D(uVelocity, vR).y;
  float T = texture2D(uVelocity, vT).x;
  float B = texture2D(uVelocity, vB).x;
  gl_FragColor = vec4(0.5 * (R - L - T + B), 0.0, 0.0, 1.0);
}`;

const FS_VORTICITY = `
precision highp float;
precision highp sampler2D;
varying vec2 vUv;
varying vec2 vL; varying vec2 vR; varying vec2 vT; varying vec2 vB;
uniform sampler2D uVelocity;
uniform sampler2D uCurl;
uniform float curl;
uniform float dt;
void main() {
  float L = texture2D(uCurl, vL).x;
  float R = texture2D(uCurl, vR).x;
  float T = texture2D(uCurl, vT).x;
  float B = texture2D(uCurl, vB).x;
  float C = texture2D(uCurl, vUv).x;
  vec2 force = 0.5 * vec2(abs(T) - abs(B), abs(R) - abs(L));
  force /= length(force) + 0.0001;
  force *= curl * C;
  force.y *= -1.0;
  vec2 vel = texture2D(uVelocity, vUv).xy;
  vel += force * dt;
  vel = min(max(vel, -1000.0), 1000.0);
  gl_FragColor = vec4(vel, 0.0, 1.0);
}`;

const FS_PRESSURE = `
precision mediump float;
precision mediump sampler2D;
varying vec2 vUv;
varying vec2 vL; varying vec2 vR; varying vec2 vT; varying vec2 vB;
uniform sampler2D uPressure;
uniform sampler2D uDivergence;
void main() {
  float L = texture2D(uPressure, vL).x;
  float R = texture2D(uPressure, vR).x;
  float T = texture2D(uPressure, vT).x;
  float B = texture2D(uPressure, vB).x;
  float divergence = texture2D(uDivergence, vUv).x;
  gl_FragColor = vec4((L + R + B + T - divergence) * 0.25, 0.0, 0.0, 1.0);
}`;

const FS_GRADIENT = `
precision mediump float;
precision mediump sampler2D;
varying vec2 vUv;
varying vec2 vL; varying vec2 vR; varying vec2 vT; varying vec2 vB;
uniform sampler2D uPressure;
uniform sampler2D uVelocity;
void main() {
  float L = texture2D(uPressure, vL).x;
  float R = texture2D(uPressure, vR).x;
  float T = texture2D(uPressure, vT).x;
  float B = texture2D(uPressure, vB).x;
  vec2 velocity = texture2D(uVelocity, vUv).xy;
  velocity -= vec2(R - L, T - B);
  gl_FragColor = vec4(velocity, 0.0, 1.0);
}`;

const compileShader = (gl, type, source) => {
  const s = gl.createShader(type);
  gl.shaderSource(s, source);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    return null;
  }
  return s;
};

const createProgram = (gl, vsSrc, fsSrc) => {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vsSrc);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSrc);
  if (!vs || !fs) return null;
  const p = gl.createProgram();
  gl.attachShader(p, vs);
  gl.attachShader(p, fs);
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    return null;
  }
  const uniforms = {};
  const count = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
  for (let i = 0; i < count; i += 1) {
    const info = gl.getActiveUniform(p, i);
    uniforms[info.name] = gl.getUniformLocation(p, info.name);
  }
  return { program: p, uniforms };
};

const getWebGLContext = (canvas) => {
  const params = {
    alpha: true,
    depth: false,
    stencil: false,
    antialias: false,
    preserveDrawingBuffer: false,
    premultipliedAlpha: false,
  };
  let gl = canvas.getContext('webgl2', params);
  const isWebGL2 = !!gl;
  if (!gl) {
    gl = canvas.getContext('webgl', params) || canvas.getContext('experimental-webgl', params);
  }
  if (!gl) return null;

  let halfFloatTexType;
  let supportLinear;
  let internalRG;
  let internalR;
  let formatRG;
  let formatR;
  let internalRGBA;
  let formatRGBA;

  if (isWebGL2) {
    gl.getExtension('EXT_color_buffer_float');
    supportLinear = !!gl.getExtension('OES_texture_float_linear');
    halfFloatTexType = gl.HALF_FLOAT;
    internalRGBA = gl.RGBA16F;
    formatRGBA = gl.RGBA;
    internalRG = gl.RG16F;
    formatRG = gl.RG;
    internalR = gl.R16F;
    formatR = gl.RED;
  } else {
    const halfFloat = gl.getExtension('OES_texture_half_float');
    supportLinear = !!gl.getExtension('OES_texture_half_float_linear');
    if (!halfFloat) return null;
    halfFloatTexType = halfFloat.HALF_FLOAT_OES;
    internalRGBA = gl.RGBA;
    formatRGBA = gl.RGBA;
    internalRG = gl.RGBA;
    formatRG = gl.RGBA;
    internalR = gl.RGBA;
    formatR = gl.RGBA;
  }

  return {
    gl,
    isWebGL2,
    halfFloatTexType,
    supportLinear,
    internalRGBA,
    formatRGBA,
    internalRG,
    formatRG,
    internalR,
    formatR,
  };
};

const createFBO = (gl, w, h, internalFormat, format, type, filter) => {
  gl.activeTexture(gl.TEXTURE0);
  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, w, h, 0, format, type, null);
  const fbo = gl.createFramebuffer();
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  gl.viewport(0, 0, w, h);
  gl.clear(gl.COLOR_BUFFER_BIT);
  return {
    texture: tex,
    fbo,
    width: w,
    height: h,
    texelSizeX: 1 / w,
    texelSizeY: 1 / h,
    attach(id) {
      gl.activeTexture(gl.TEXTURE0 + id);
      gl.bindTexture(gl.TEXTURE_2D, tex);
      return id;
    },
  };
};

const createDoubleFBO = (gl, w, h, internalFormat, format, type, filter) => {
  let fbo1 = createFBO(gl, w, h, internalFormat, format, type, filter);
  let fbo2 = createFBO(gl, w, h, internalFormat, format, type, filter);
  return {
    width: w,
    height: h,
    texelSizeX: 1 / w,
    texelSizeY: 1 / h,
    get read() {
      return fbo1;
    },
    get write() {
      return fbo2;
    },
    swap() {
      const t = fbo1;
      fbo1 = fbo2;
      fbo2 = t;
    },
  };
};

const FluidHero = ({ onFail }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = getWebGLContext(canvas);
    if (!ctx) {
      if (onFail) onFail();
      return undefined;
    }
    const {
      gl, supportLinear, halfFloatTexType,
      internalRGBA, formatRGBA, internalRG, formatRG, internalR, formatR,
    } = ctx;

    const filter = supportLinear ? gl.LINEAR : gl.NEAREST;

    const quadBuf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    const clearProg = createProgram(gl, VS, FS_CLEAR);
    const copyProg = createProgram(gl, VS, FS_COPY);
    const displayProg = createProgram(gl, VS, FS_DISPLAY);
    const splatProg = createProgram(gl, VS, FS_SPLAT);
    const advectProg = createProgram(gl, VS, FS_ADVECTION);
    const divergProg = createProgram(gl, VS, FS_DIVERGENCE);
    const curlProg = createProgram(gl, VS, FS_CURL);
    const vorticityProg = createProgram(gl, VS, FS_VORTICITY);
    const pressureProg = createProgram(gl, VS, FS_PRESSURE);
    const gradProg = createProgram(gl, VS, FS_GRADIENT);

    if (
      !copyProg || !clearProg || !displayProg || !splatProg || !advectProg ||
      !divergProg || !curlProg || !vorticityProg || !pressureProg || !gradProg
    ) {
      if (onFail) onFail();
      return undefined;
    }

    let dye;
    let velocity;
    let divergence;
    let curl;
    let pressure;

    const getRes = (target) => {
      const aspect = gl.drawingBufferWidth / gl.drawingBufferHeight;
      const min = Math.round(target);
      const max = Math.round(target * (aspect < 1 ? 1 / aspect : aspect));
      return aspect < 1 ? { width: min, height: max } : { width: max, height: min };
    };

    const initFBOs = () => {
      const simRes = getRes(CFG.SIM_RESOLUTION);
      const dyeRes = getRes(CFG.DYE_RESOLUTION);
      gl.disable(gl.BLEND);
      dye = createDoubleFBO(gl, dyeRes.width, dyeRes.height, internalRGBA, formatRGBA, halfFloatTexType, filter);
      velocity = createDoubleFBO(gl, simRes.width, simRes.height, internalRG, formatRG, halfFloatTexType, filter);
      divergence = createFBO(gl, simRes.width, simRes.height, internalR, formatR, halfFloatTexType, gl.NEAREST);
      curl = createFBO(gl, simRes.width, simRes.height, internalR, formatR, halfFloatTexType, gl.NEAREST);
      pressure = createDoubleFBO(gl, simRes.width, simRes.height, internalR, formatR, halfFloatTexType, gl.NEAREST);
    };

    const resize = () => {
      const cssW = canvas.clientWidth;
      const cssH = canvas.clientHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      const w = Math.max(1, Math.floor(cssW * dpr));
      const h = Math.max(1, Math.floor(cssH * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        initFBOs();
      }
    };

    const blit = (target) => {
      if (target == null) {
        gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      } else {
        gl.viewport(0, 0, target.width, target.height);
        gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo);
      }
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    };

    const splat = (x, y, dx, dy, color) => {
      gl.useProgram(splatProg.program);
      gl.uniform1i(splatProg.uniforms.uTarget, velocity.read.attach(0));
      gl.uniform1f(splatProg.uniforms.aspectRatio, canvas.width / canvas.height);
      gl.uniform2f(splatProg.uniforms.point, x, y);
      gl.uniform3f(splatProg.uniforms.color, dx, -dy, 0);
      gl.uniform1f(splatProg.uniforms.radius, CFG.SPLAT_RADIUS / 100);
      blit(velocity.write);
      velocity.swap();

      gl.uniform1i(splatProg.uniforms.uTarget, dye.read.attach(0));
      gl.uniform3f(splatProg.uniforms.color, color[0], color[1], color[2]);
      blit(dye.write);
      dye.swap();
    };

    const step = (dt) => {
      gl.disable(gl.BLEND);

      gl.useProgram(curlProg.program);
      gl.uniform2f(curlProg.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
      gl.uniform1i(curlProg.uniforms.uVelocity, velocity.read.attach(0));
      blit(curl);

      gl.useProgram(vorticityProg.program);
      gl.uniform2f(vorticityProg.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
      gl.uniform1i(vorticityProg.uniforms.uVelocity, velocity.read.attach(0));
      gl.uniform1i(vorticityProg.uniforms.uCurl, curl.attach(1));
      gl.uniform1f(vorticityProg.uniforms.curl, CFG.CURL);
      gl.uniform1f(vorticityProg.uniforms.dt, dt);
      blit(velocity.write);
      velocity.swap();

      gl.useProgram(divergProg.program);
      gl.uniform2f(divergProg.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
      gl.uniform1i(divergProg.uniforms.uVelocity, velocity.read.attach(0));
      blit(divergence);

      gl.useProgram(clearProg.program);
      gl.uniform1i(clearProg.uniforms.uTexture, pressure.read.attach(0));
      gl.uniform1f(clearProg.uniforms.value, CFG.PRESSURE);
      blit(pressure.write);
      pressure.swap();

      gl.useProgram(pressureProg.program);
      gl.uniform2f(pressureProg.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
      gl.uniform1i(pressureProg.uniforms.uDivergence, divergence.attach(0));
      for (let i = 0; i < CFG.PRESSURE_ITERATIONS; i += 1) {
        gl.uniform1i(pressureProg.uniforms.uPressure, pressure.read.attach(1));
        blit(pressure.write);
        pressure.swap();
      }

      gl.useProgram(gradProg.program);
      gl.uniform2f(gradProg.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
      gl.uniform1i(gradProg.uniforms.uPressure, pressure.read.attach(0));
      gl.uniform1i(gradProg.uniforms.uVelocity, velocity.read.attach(1));
      blit(velocity.write);
      velocity.swap();

      gl.useProgram(advectProg.program);
      gl.uniform2f(advectProg.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
      gl.uniform2f(advectProg.uniforms.dyeTexelSize, velocity.texelSizeX, velocity.texelSizeY);
      const velId = velocity.read.attach(0);
      gl.uniform1i(advectProg.uniforms.uVelocity, velId);
      gl.uniform1i(advectProg.uniforms.uSource, velId);
      gl.uniform1f(advectProg.uniforms.dt, dt);
      gl.uniform1f(advectProg.uniforms.dissipation, CFG.VELOCITY_DISSIPATION);
      blit(velocity.write);
      velocity.swap();

      gl.uniform2f(advectProg.uniforms.dyeTexelSize, dye.texelSizeX, dye.texelSizeY);
      gl.uniform1i(advectProg.uniforms.uVelocity, velocity.read.attach(0));
      gl.uniform1i(advectProg.uniforms.uSource, dye.read.attach(1));
      gl.uniform1f(advectProg.uniforms.dissipation, CFG.DENSITY_DISSIPATION);
      blit(dye.write);
      dye.swap();
    };

    const renderFluid = () => {
      gl.useProgram(displayProg.program);
      gl.uniform1i(displayProg.uniforms.uTexture, dye.read.attach(0));
      blit(null);
    };

    const pointers = new Map();
    const pointerMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const px = (e.clientX - rect.left) / rect.width;
      const py = 1 - (e.clientY - rect.top) / rect.height;
      const last = pointers.get('mouse') || { x: px, y: py, color: randSplatColor(), lastT: performance.now() };
      const dx = (px - last.x) * CFG.SPLAT_FORCE;
      const dy = (py - last.y) * CFG.SPLAT_FORCE;
      splat(px, py, dx, dy, last.color);
      let color = last.color;
      const now = performance.now();
      if (now - last.lastT > 1400) color = randSplatColor();
      pointers.set('mouse', { x: px, y: py, color, lastT: now });
    };
    canvas.addEventListener('mousemove', pointerMove);

    const touchPos = (touch) => {
      const rect = canvas.getBoundingClientRect();
      return {
        x: (touch.clientX - rect.left) / rect.width,
        y: 1 - (touch.clientY - rect.top) / rect.height,
      };
    };
    const onTouchStart = (e) => {
      Array.from(e.changedTouches).forEach((t) => {
        const p = touchPos(t);
        const color = randSplatColor();
        pointers.set(t.identifier, { ...p, color, lastT: performance.now() });
        const brighter = [color[0] * 2.6, color[1] * 2.6, color[2] * 2.6];
        splat(p.x, p.y, (Math.random() - 0.5) * 1200, (Math.random() - 0.5) * 1200, brighter);
      });
    };
    const onTouchMove = (e) => {
      Array.from(e.changedTouches).forEach((t) => {
        const prev = pointers.get(t.identifier);
        if (!prev) return;
        const p = touchPos(t);
        const dx = (p.x - prev.x) * CFG.SPLAT_FORCE;
        const dy = (p.y - prev.y) * CFG.SPLAT_FORCE;
        splat(p.x, p.y, dx, dy, prev.color);
        pointers.set(t.identifier, { ...prev, ...p });
      });
    };
    const onTouchEnd = (e) => {
      Array.from(e.changedTouches).forEach((t) => pointers.delete(t.identifier));
    };
    canvas.addEventListener('touchstart', onTouchStart, { passive: true });
    canvas.addEventListener('touchmove', onTouchMove, { passive: true });
    canvas.addEventListener('touchend', onTouchEnd);
    canvas.addEventListener('touchcancel', onTouchEnd);

    const seedSplats = (n) => {
      for (let i = 0; i < n; i += 1) {
        const c = randSplatColor();
        const cb = [c[0] * 2.4, c[1] * 2.4, c[2] * 2.4];
        const x = 0.15 + Math.random() * 0.7;
        const y = 0.2 + Math.random() * 0.6;
        const dx = (Math.random() - 0.5) * 1400;
        const dy = (Math.random() - 0.5) * 1400;
        splat(x, y, dx, dy, cb);
      }
    };

    let autoSplatTimer = 0;
    const maybeAutoSplat = (dt) => {
      autoSplatTimer += dt * 1000;
      if (autoSplatTimer >= CFG.AUTO_SPLAT_INTERVAL) {
        autoSplatTimer = 0;
        const c = randSplatColor();
        const cb = [c[0] * 1.4, c[1] * 1.4, c[2] * 1.4];
        const x = 0.1 + Math.random() * 0.8;
        const y = 0.15 + Math.random() * 0.7;
        const dx = (Math.random() - 0.5) * 1200;
        const dy = (Math.random() - 0.5) * 1200;
        splat(x, y, dx, dy, cb);
      }
    };

    let raf;
    let prevT = performance.now();
    let alive = true;

    const loop = (t) => {
      if (!alive) return;
      resize();
      const dt = Math.min(0.0166, (t - prevT) / 1000);
      prevT = t;
      const r = canvas.getBoundingClientRect();
      if (r.bottom > 0 && r.top < window.innerHeight) {
        maybeAutoSplat(dt);
        step(dt);
        renderFluid();
      }
      raf = window.requestAnimationFrame(loop);
    };

    resize();
    seedSplats(8);
    raf = window.requestAnimationFrame(loop);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.cancelAnimationFrame(raf);
      alive = false;
      resize();
      seedSplats(12);
      step(0.0166);
      renderFluid();
    }

    return () => {
      alive = false;
      window.cancelAnimationFrame(raf);
      canvas.removeEventListener('mousemove', pointerMove);
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onTouchEnd);
      canvas.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [onFail]);

  return <canvas ref={canvasRef} className="fh-canvas" aria-hidden="true" />;
};

/* ──────────────────────────────────────────────────────────────
   LIVE DEPLOY CONSOLE — plausible agent-activity feed, ticks ~2.4s
   ────────────────────────────────────────────────────────────── */
const CONSOLE_SCRIPT = [
  { t: 'koda', verb: 'pushed', what: 'fix(api): handle empty payload in /trades' },
  { t: 'iris', verb: 'designed', what: '12 portfolio variants for /work' },
  { t: 'rex', verb: 'deployed', what: 'axyonex-execution-engine v2.4.1' },
  { t: 'stacy', verb: 'patched', what: 'CVE-2026-1234 across 4 services' },
  { t: 'koda', verb: 'merged', what: 'feat(blog): MDX migration #482' },
  { t: 'vance', verb: 'shipped', what: 'Q2 launch plan to founders' },
  { t: 'zara', verb: 'green', what: '247/247 tests passing on main' },
  { t: 'iris', verb: 'updated', what: 'design-system: --accent 0.04 up' },
  { t: 'rex', verb: 'reviewed', what: 'PR #1289 (Fortress dashboard)' },
  { t: 'koda', verb: 'optimized', what: 'hero canvas: 312 to 1.3ms / frame' },
  { t: 'jacob', verb: 'closed', what: 'client kickoff for project Lume' },
  { t: 'tanush', verb: 'committed', what: 'docs(strategy): 2026-h2 roadmap' },
];

const AGENT_COLORS = {
  koda: '#f472d0',
  iris: '#a78bfa',
  rex: '#34d399',
  stacy: '#fb923c',
  vance: '#60a5fa',
  zara: '#facc15',
  jacob: '#cbd5e1',
  tanush: '#ffffff',
};

const nowStamp = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const DeployConsole = () => {
  const [lines, setLines] = useState([]);
  const idxRef = useRef(0);

  useEffect(() => {
    const seed = [];
    for (let i = 0; i < 4; i += 1) {
      const e = CONSOLE_SCRIPT[i % CONSOLE_SCRIPT.length];
      seed.push({ ...e, time: nowStamp(), id: `${i}-${e.what}` });
    }
    setLines(seed);
    idxRef.current = 4;

    const interval = setInterval(() => {
      const e = CONSOLE_SCRIPT[idxRef.current % CONSOLE_SCRIPT.length];
      idxRef.current += 1;
      const id = `${idxRef.current}-${e.what}`;
      setLines((prev) => [...prev, { ...e, time: nowStamp(), id }].slice(-6));
    }, 2400);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="fh-console">
      <div className="fh-console-bar">
        <div className="fh-console-dots">
          <span style={{ background: '#ff5f57' }} />
          <span style={{ background: '#febc2e' }} />
          <span style={{ background: '#28c840' }} />
        </div>
        <div className="fh-console-title">~/feral · agents.live</div>
        <div className="fh-console-status">
          <span className="fh-status-pulse" />
          LIVE
        </div>
      </div>
      <div className="fh-console-body">
        {lines.map((l, i) => (
          <div
            key={l.id}
            className="fh-console-line"
            style={{ opacity: 0.45 + (i / lines.length) * 0.55 }}
          >
            <span className="fh-cl-time">{l.time}</span>
            <span className="fh-cl-agent" style={{ color: AGENT_COLORS[l.t] || '#fff' }}>
              {l.t}
            </span>
            <span className="fh-cl-verb">{l.verb}</span>
            <span className="fh-cl-what">{l.what}</span>
          </div>
        ))}
        <div className="fh-console-line fh-console-prompt">
          <span className="fh-cl-prompt">$</span>
          <span className="fh-cl-caret" />
        </div>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────
   STAT TICKER — counts up on mount
   ────────────────────────────────────────────────────────────── */
const CountUp = ({ to, suffix = '', duration = 1400, decimals = 0 }) => {
  const [v, setV] = useState(0);

  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setV(to * eased);
      if (t < 1) raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [to, duration]);

  return (
    <>
      {v.toFixed(decimals)}
      {suffix}
    </>
  );
};

const HERO_STATS = [
  { label: 'apps shipped', value: 15, suffix: '+' },
  { label: 'platforms live', value: 4, suffix: '' },
  { label: 'production years', value: 6, suffix: '' },
  { label: 'ms p99 latency', value: 0.7, suffix: '', decimals: 1 },
];

const HEADLINE_LINES = [
  { text: 'We design', variant: 'plain' },
  { text: 'software', variant: 'grad' },
  { text: 'that feels', variant: 'plain' },
  { text: 'inevitable.', variant: 'italic' },
];

const Hero = () => {
  const [fluidFailed, setFluidFailed] = useState(false);
  return (
  <section className="fh-hero" id="home">
    {fluidFailed ? <FlowField /> : <FluidHero onFail={() => setFluidFailed(true)} />}
    <div className="fh-noise" aria-hidden="true" />

    <div className="r-wrap fh-inner">
      <div className="fh-left">
        <motion.div
          className="fh-pill"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="fh-pill-dot" />
          Available for projects · Q3 — Q4 2026
        </motion.div>

        <h1 className="fh-headline" aria-label="We design software that feels inevitable.">
          {HEADLINE_LINES.map((line, i) => (
            <motion.span
              key={line.text}
              className={`fh-hl-line fh-hl-${line.variant}`}
              initial={{ opacity: 0, y: 28, filter: 'blur(8px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ delay: 0.15 + i * 0.12, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              {line.text}
            </motion.span>
          ))}
        </h1>

        <motion.p
          className="fh-lede"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          An eight-person, AI-augmented studio for teams who need the first impression to
          feel <em>expensive, inevitable, and impossible to ignore</em> — without
          sacrificing clarity, speed, or conversion.
        </motion.p>

        <motion.div
          className="fh-actions"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <a href="#portfolio" className="btn-primary fh-cta">
            <span>See what we ship</span>
            <ArrowRight className="w-4 h-4" />
          </a>
          <a href="#contact" className="btn-secondary fh-cta">
            <span>Start a project</span>
          </a>
        </motion.div>

        <motion.div
          className="fh-stats"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          {HERO_STATS.map((s) => (
            <div key={s.label} className="fh-stat">
              <div className="fh-stat-v">
                <CountUp to={s.value} suffix={s.suffix} decimals={s.decimals || 0} />
              </div>
              <div className="fh-stat-l">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.div
        className="fh-right"
        initial={{ opacity: 0, y: 28, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay: 0.4, duration: 1, ease: [0.16, 1, 0.3, 1] }}
      >
        <DeployConsole />
      </motion.div>
    </div>

    <div className="fh-scrollcue" aria-hidden="true">
      <span>scroll</span>
      <div className="fh-scrollcue-rail">
        <div className="fh-scrollcue-dot" />
      </div>
    </div>
  </section>
  );
};

export default Hero;
